import React from 'react';
import { Chip, Stack, Typography } from '@mui/material';

export const formatPurgeDate = (iso: string | null | undefined) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export const organizationStatusLabel = (
  org: { status?: string; purgeScheduledAt?: string; purgeStatus?: string },
) => {
  const status = org.status || 'ACTIVE';
  if (status === 'DELETED') {
    return `Will Hard Delete on ${formatPurgeDate(org.purgeScheduledAt)}`;
  }
  if (status === 'PURGING') {
    return org.purgeStatus === 'failed' ? 'Deletion failed' : 'Deletion in progress…';
  }
  if (status === 'PURGED') return 'Purged';
  return null;
};

export const portalStatusLabel = (portal: { status?: string; purgeStatus?: string }) => {
  const status = portal.status || 'ACTIVE';
  if (status === 'PURGING') {
    return portal.purgeStatus === 'failed' ? 'Deletion failed' : 'Deletion in progress…';
  }
  if (status === 'PURGED') return 'Purged';
  return null;
};

const purgeChipColor = (status: string, purgeStatus?: string) => {
  if (purgeStatus === 'failed') return 'error' as const;
  if (status === 'PURGING') return 'warning' as const;
  return 'default' as const;
};

const ActiveChip = () => (
  <Chip
    size="small"
    variant="outlined"
    label="Active"
    color="success"
    // The global MuiChip theme forces a solid background + white label, so
    // override locally to render a true outlined chip with green text/border.
    sx={{
      backgroundColor: 'transparent',
      border: '1px solid',
      borderColor: 'success.main',
      '& .MuiChip-label': { color: 'success.main' },
    }}
  />
);

export const OrganizationDeletionStatusFormatter = ({
  item,
}: {
  value?: string;
  item: { status?: string; purgeScheduledAt?: string; purgeStatus?: string };
}) => {
  const status = item.status || 'ACTIVE';
  const label = organizationStatusLabel(item);

  if (status === 'ACTIVE' && !label) {
    return <ActiveChip />;
  }

  if (status === 'DELETED') {
    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Chip size="small" variant="outlined" label="Deleted" color="error" />
        {item.purgeScheduledAt && (
          <Typography variant="caption" color="text.secondary">
            Hard delete
            {' '}
            {formatPurgeDate(item.purgeScheduledAt)}
          </Typography>
        )}
      </Stack>
    );
  }

  if (!label) {
    return <ActiveChip />;
  }

  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      color={purgeChipColor(status, item.purgeStatus)}
    />
  );
};

export const PortalDeletionStatusFormatter = ({
  item,
}: {
  value?: string;
  item: { status?: string; purgeStatus?: string };
}) => {
  const label = portalStatusLabel(item);
  const status = item.status || 'ACTIVE';
  if (!label) {
    return <ActiveChip />;
  }
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      color={purgeChipColor(status, item.purgeStatus)}
    />
  );
};
